// Marcador presencial (lote 6, item 5): as chamadas à API e o placar ao vivo. Quem abre o link (/placar/CODIGO) não
// precisa de conta: o placar chega pelo EventSource da API e, se a conexão cair, pela leitura a cada 5 s.
import type { Lado, Placar } from '@truco6/marcador';
import { ErroDaApi, api } from './api.ts';
import type { Sessao } from './conta.ts';

export interface LadoDoMarcador { nome: string; trio: { id: string; escudo: string } | null }
export interface PartidaDaNoite { pontos: [number, number]; vencedor: Lado; em: number }
export interface PlacarAoVivo {
  codigo: string;
  lados: [LadoDoMarcador, LadoDoMarcador];
  placar: Placar;
  partidas: PartidaDaNoite[];
  caneta: string; // apelido de quem marca
  criadaEm: number;
  atualizadaEm: number;
  encerrada: boolean;
  versao: number;
}
export interface PlacarDeQuemMarca extends PlacarAoVivo { souCaneta: boolean; souDono: boolean }
export interface NoiteNoHistorico { codigo: string; criadaEm: number; lados: [LadoDoMarcador, LadoDoMarcador]; vitorias: [number, number]; partidas: PartidaDaNoite[]; aberta: boolean }
export type EscolhaDoLado = { nome: string } | { trio: string };
export type Acao = { acao: 'somar'; lado: Lado; soma: number } | { acao: 'desfazer' } | { acao: 'nova_partida' } | { acao: 'renomear'; lado: Lado; nome: string };

export const linkDoPlacar = (codigo: string) => `${location.origin}/placar/${codigo}`;
export const ehCodigoDoPlacar = (c: string) => /^[A-HJ-NP-Z2-9]{8}$/.test(c);

export const criarPlacar = (s: Sessao, lados: [EscolhaDoLado, EscolhaDoLado]) => api<PlacarDeQuemMarca & { link: string }>(s, 'POST', '/marcador', { lados });
export const marcar = (s: Sessao, codigo: string, a: Acao, versao: number) => api<PlacarDeQuemMarca>(s, 'POST', `/marcador/${codigo}/marcar`, { ...a, versao });
export const passarCaneta = (s: Sessao, codigo: string) => api<{ link: string; ate: number }>(s, 'POST', `/marcador/${codigo}/caneta`);
export const pegarCaneta = (s: Sessao, codigo: string, token: string) => api<PlacarDeQuemMarca>(s, 'POST', `/marcador/${codigo}/caneta/pegar`, { token });
export const retomarCaneta = (s: Sessao, codigo: string) => api<PlacarDeQuemMarca>(s, 'POST', `/marcador/${codigo}/caneta/retomar`);
export const encerrarNoite = (s: Sessao, codigo: string) => api<PlacarDeQuemMarca>(s, 'POST', `/marcador/${codigo}/encerrar`);
export const lerHistorico = (s: Sessao) => api<{ noites: NoiteNoHistorico[] }>(s, 'GET', '/marcador/noites');

// Com conta: diz se a pessoa tem a caneta. Sem conta: só o placar.
export async function verPlacar(s: Sessao | null, codigo: string): Promise<PlacarDeQuemMarca> {
  if (s) return api<PlacarDeQuemMarca>(s, 'GET', `/marcador/${codigo}`);
  const r = await fetch(`/api/marcador/${codigo}`).catch(() => null);
  if (!r) throw new ErroDaApi(0, 'sem_conexao', 'Sem conexão com o servidor. Tente de novo.');
  const d = (await r.json().catch(() => null)) as (PlacarDeQuemMarca & { erro?: { codigo: string; mensagem: string } }) | null;
  if (r.ok && d) return { ...d, souCaneta: false, souDono: false };
  throw new ErroDaApi(r.status, d?.erro?.codigo ?? 'erro', d?.erro?.mensagem ?? 'Não deu para abrir o placar.');
}

// Ao vivo: chama f a cada placar novo. Devolve como parar.
export function ouvirPlacar(codigo: string, f: (p: PlacarAoVivo) => void): () => void {
  let fechado = false, reserva: ReturnType<typeof setInterval> | null = null, fonte: EventSource | null = null;
  const ler = () => { fetch(`/api/marcador/${codigo}`).then((r) => (r.ok ? r.json() : null)).then((p: PlacarAoVivo | null) => { if (p && !fechado) f(p); }, () => {}); };
  if (typeof EventSource !== 'undefined') {
    fonte = new EventSource(`/api/marcador/${codigo}/ao-vivo`);
    fonte.addEventListener('placar', (ev) => { try { f(JSON.parse((ev as MessageEvent<string>).data) as PlacarAoVivo); } catch { /* evento estragado */ } });
    // Caiu: o navegador tenta de novo sozinho; enquanto isso, a leitura a cada 5 s segura o placar.
    fonte.onerror = () => { if (!reserva && !fechado) reserva = setInterval(ler, 5000); };
    fonte.onopen = () => { if (reserva) { clearInterval(reserva); reserva = null; } };
  } else reserva = setInterval(ler, 5000);
  return () => { fechado = true; fonte?.close(); if (reserva) clearInterval(reserva); };
}

// Tela acesa enquanto a tela do marcador está aberta, quando o aparelho deixa. Devolve se conseguiu.
type Trava = { release(): Promise<void>; addEventListener(e: 'release', f: () => void): void };
export function manterTelaAcesa(aoMudar: (acesa: boolean) => void): () => void {
  let trava: Trava | null = null, vivo = true;
  const wl = (navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<Trava> } }).wakeLock;
  async function pedir() {
    if (!wl || document.visibilityState !== 'visible' || trava || !vivo) return;
    try {
      trava = await wl.request('screen');
      aoMudar(true);
      trava.addEventListener('release', () => { trava = null; aoMudar(false); });
    } catch { /* o aparelho não deixou */ }
  }
  const voltou = () => { if (document.visibilityState === 'visible') void pedir(); };
  document.addEventListener('visibilitychange', voltou);
  void pedir();
  return () => { vivo = false; document.removeEventListener('visibilitychange', voltou); void trava?.release().catch(() => {}); };
}
